import React, { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { fetchDealById } from 'submission/lib/deal-api'
import { ProjectBreadcrumbs } from 'components/Projects/project-breadcrumbs'
import { Navbar } from 'components/navbar'
import { Footer } from 'components/footer'
import { isEquityDeal } from './equity'
import Settings from './Settings'
import RoundsTable from './RoundsTable'
import EntriesTable from './EntriesTable'
import useShortlistRounds from 'hooks/useShortlistRounds'

const Shortlist: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>()
  const [deal, setDeal] = useState<any | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedRoundId, setSelectedRoundId] = useState<number | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  const { rounds, mutate: mutateRounds } = useShortlistRounds(projectId ?? null)

  useEffect(() => {
    if (!projectId) return
    let cancelled = false
    setLoading(true)
    setError(null)
    fetchDealById(projectId)
      .then((d) => {
        if (!cancelled) setDeal(d)
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [projectId, reloadKey])

  const openRound = useMemo(
    () => rounds.find((r) => r.status === 'open') ?? null,
    [rounds],
  )
  useEffect(() => {
    if (selectedRoundId === null && openRound) {
      setSelectedRoundId(openRound.id)
    }
  }, [openRound, selectedRoundId])

  if (!projectId) {
    return <div className="p-8">Missing project id.</div>
  }

  if (loading) {
    return <div className="p-8">Loading…</div>
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="text-red-600 mb-4">Failed to load deal: {error}</div>
        <button
          className="px-4 py-2 border rounded"
          onClick={() => setReloadKey((k) => k + 1)}
        >
          Retry
        </button>
      </div>
    )
  }

  if (!deal) {
    return <div className="p-8">Deal not found.</div>
  }

  if (!isEquityDeal(deal)) {
    return (
      <div className="p-8">
        <p className="mb-4">
          This deal is not an equity deal — shortlisting does not apply.
        </p>
        <Link to={`/deals/${projectId}`} className="text-blue-600 underline">
          Back to deal
        </Link>
      </div>
    )
  }

  const projectName = deal?.metadata?.overview?.projectName ?? 'Deal'

  return (
    <div className="flex flex-col min-h-screen bg-neutral-white font-sans text-slate-400">
      <Navbar />
      <ProjectBreadcrumbs projectTitle={projectName} />
      <main className="flex-1 flex flex-col bg-neutral-50 p-6 gap-6">
        <h1 className="text-2xl font-semibold text-slate-900">
          {projectName} — Shortlist
        </h1>
        <Settings deal={deal} onSaved={(updated) => setDeal(updated)} />
        <RoundsTable
          dealId={projectId}
          deal={deal}
          rounds={rounds}
          mutateRounds={mutateRounds}
          selectedRoundId={selectedRoundId}
          onSelectRound={setSelectedRoundId}
        />
        {selectedRoundId !== null && (
          <EntriesTable
            dealId={projectId}
            roundId={selectedRoundId}
            round={rounds.find((r) => r.id === selectedRoundId) ?? null}
            mutateRounds={mutateRounds}
          />
        )}
      </main>
      <Footer />
    </div>
  )
}

export default Shortlist
